/**
 * Diálogo de dano à saúde: título e descrição. Grava na hora.
 */
import React, { useState } from 'react';
import { Grid, TextField } from '@mui/material';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicDamage } from '../../../../../types/ergonomicWorkstation';
import { dialogFieldSx } from './dialogHelpers';
import { ItemDialog } from './ItemDialog';
import { useItemSubmit } from './useItemSubmit';

type DamageField = 'title' | 'description';

const SERVER_LABELS: ReadonlyArray<[string, DamageField]> = [
  ['Dano à saúde', 'title'],
  ['Descrição', 'description'],
];

interface DamageDialogProps {
  workstationId: number;
  item: ErgonomicDamage | null;
  onSaved: (message: string) => void;
  onClose: () => void;
}

export const DamageDialog: React.FC<DamageDialogProps> = ({ workstationId, item, onSaved, onClose }) => {
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const { submitting, error, errors, setErrors, run } = useItemSubmit<DamageField>(
    SERVER_LABELS,
    'Não foi possível guardar o dano. Tente de novo em alguns minutos.'
  );

  const submit = async () => {
    const found: Partial<Record<DamageField, string>> = {};
    if (title.trim() === '') found.title = 'Diga qual é o dano à saúde.';
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const saved = await run(async () => {
      const payload = { title: title.trim(), description: description.trim() === '' ? null : description.trim() };
      if (item) await ergonomicWorkstationService.updateDamage(workstationId, item.id, payload);
      else await ergonomicWorkstationService.createDamage(workstationId, payload);
    });
    if (saved) onSaved(item ? 'Dano atualizado.' : 'Dano guardado.');
  };

  return (
    <ItemDialog
      title={item ? 'Editar dano à saúde' : 'Novo dano à saúde'}
      submitLabel="Guardar dano"
      submitting={submitting}
      error={error}
      onSubmit={submit}
      onClose={onClose}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            required
            label="Dano à saúde"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            error={Boolean(errors.title)}
            helperText={errors.title || 'Ex.: Lombalgias'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Descrição"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            error={Boolean(errors.description)}
            helperText={errors.description || 'Opcional. Ex.: Dor na região lombar que pode irradiar para as pernas.'}
            disabled={submitting}
            sx={dialogFieldSx}
          />
        </Grid>
      </Grid>
    </ItemDialog>
  );
};
